import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import API from "config/ApiConfig";
import { Mosaic } from "react-loading-indicators";

// constants
// const API_BASE_URL = 'http://localhost:5000/api/admin';
const API_BASE_URL = API.API_ADMIN;
function getToken() {
  return localStorage.getItem('authToken');
}

// Fungsi untuk mendapatkan header dengan token JWT
function getAuthHeaders() {
  const token = getToken();
  if (!token) {
      throw new Error('No token available');
  }
  return {
      'Authorization': `Bearer ${token}`, // Format token JWT
      'Content-Type': 'application/json' // Jika perlu, untuk menentukan jenis konten
  };
}

export default function CardTable({ color }) {
  const [dataRekap, setDataRekap] = useState([]);
  const [tentors, setTentors] = useState({});
  const [siswa, setSiswa] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // New state for sorting order

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); // Toggle sort order
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/rekap/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          setDataRekap(dataRekap.filter(item => item._id !== id));
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      } catch (error) {
        setError('Failed to delete record.');
        console.error('Error deleting record:', error);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch siswa data
        const siswaResponse = await fetch(`${API_BASE_URL}/siswa`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!siswaResponse.ok) {
          throw new Error(`HTTP error! Status: ${siswaResponse.status}`);
        }
        const siswaData = await siswaResponse.json();

        // Fetch rekap data
        const rekapResponse = await fetch(`${API_BASE_URL}/rekap`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!rekapResponse.ok) {
          throw new Error(`HTTP error! Status: ${rekapResponse.status}`);
        }
        const rekapData = await rekapResponse.json();

        // Fetch tentor data
        const tentorIds = [...new Set(rekapData.map(item => item.tentor_id))];
        const tentorRequests = tentorIds.map(id => 
          fetch(`${API_BASE_URL}/tentor/${id}`, {
            method: 'GET',
            headers: getAuthHeaders()
          }).then(response => response.json())
        );

        const tentorData = await Promise.all(tentorRequests);
        const tentorsMap = tentorData.reduce((acc, tentor) => {
          if (tentor && tentor._id) {
            acc[tentor._id] = tentor;
          }
          return acc;
        }, {});

        // Count presensi for each siswa
        const presensiCount = rekapData.reduce((acc, item) => {
          const { siswa_id } = item;
          if (!acc[siswa_id]) acc[siswa_id] = 0;
          acc[siswa_id] += 1;
          return acc;
        }, {});

        // Add presensi count to siswa
        const updatedSiswa = siswaData.reduce((acc, siswa) => {
          acc[siswa._id] = {
            ...siswa,
            presensiCount: presensiCount[siswa._id] || 0
          };
          return acc;
        }, {});

        setDataRekap(rekapData);
        setTentors(tentorsMap);
        setSiswa(updatedSiswa);

      } catch (error) {
        setError(error.message);
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="flex m-4 justify-center items-center w-screen"><Mosaic color="#1177bc" size="large" text="Loading.." textColor="" /></div>
  }
  if (error) return <div>Error: {error}</div>;

  // Filter data based on the search query
  const filteredData = Object.values(siswa).filter(siswaItem => {
    const siswaName = siswaItem.nama.toLowerCase();
    const tentorName = tentors[siswaItem.tentor_id]?.nama.toLowerCase() || '';
    const searchQueryLower = searchQuery.toLowerCase();

    return (
      siswaName.includes(searchQueryLower) ||
      tentorName.includes(searchQueryLower)
    );
  });

  // Sort data based on the sortOrder
  const sortedData = filteredData.sort((a, b) => {
    if (a.nama.toLowerCase() < b.nama.toLowerCase()) return sortOrder === 'asc' ? -1 : 1;
    if (a.nama.toLowerCase() > b.nama.toLowerCase()) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <>
      <div className="flex flex-wrap mt-4 mb-4">
        <input 
          type="text" 
          className="px-2 py-1 w-4 rounded-lg shadow space-x-2 mr-4" 
          placeholder="Cari..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Rekap Table
              </h3>
            </div>
          </div>
        </div>
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {["Nama Siswa", "Nama Tentor", "Jumlah Presensi", ""].map((heading, index) => (
                  <th
                    key={index}
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                    onClick={heading === "Nama Siswa" ? handleSort : null} // Attach sort handler
                  >
                    {heading} {heading === "Nama Siswa" ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((siswaItem) => (
                <tr key={siswaItem._id}>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4">
                    {siswaItem.nama || 'Tidak Diketahui'}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {/* Display tentors with comma separation */}
                    {siswaItem.tentor_id 
                      ? siswaItem.tentor_id
                          .map(id => tentors[id]?.nama || '')
                          .filter(name => name !== '')
                          .join(', ')
                      : 'Belum ada'}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4">
                    {siswaItem.presensiCount || 0}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4">
                    <a
                      href={`/admin/rekap/view/${siswaItem._id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <button 
                      className=" bg-lightBlue-500 text-white active:bg-lightBlue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none m-4 ease-linear transition-all duration-150"
                      >
                      <i className="fas fa-print mr-2"></i>Lihat
                      </button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

CardTable.defaultProps = {
  color: "light",
};

CardTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
};
